import React, { useState } from "react";
import { Send, CheckCircle2, AlertCircle } from "lucide-react";

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    description: "",
    website_url: "", // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // 'idle' | 'loading' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState("");

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Navn er påkrævet";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Telefonnummer er påkrævet";
    } else if (!/^[0-9\s+()-]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = "Ugyldigt telefonnummer";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email er påkrævet";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = "Ugyldig emailadresse";
    }
    if (!formData.description.trim()) {
      newErrors.description = "Beskrivelse er påkrævet";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus("loading");
    setErrorMessage("");

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok && result.status === "ok") {
        setStatus("success");
        setFormData({
          name: "",
          phone: "",
          email: "",
          description: "",
          website_url: "",
        });
      } else {
        setStatus("error");
        setErrorMessage(result.message || "Der skete en fejl under afsendelsen. Prøv venligst igen.");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setStatus("error");
      setErrorMessage("Netværksfejl. Kontroller din internetforbindelse og prøv igen.");
    }
  };

  return (
    <div className="bg-white border border-neutral-200/80 rounded-2xl p-6 md:p-10 shadow-xl shadow-brand-500/5">
      {status === "success" ? (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-100 text-brand-600 mb-6">
            <CheckCircle2 className="w-10 h-10" />
          </div>
          <h3 className="font-display text-2xl md:text-3xl font-medium text-neutral-900 mb-3">
            Tak for din besked!
          </h3>
          <p className="text-neutral-600 max-w-md mx-auto leading-relaxed">
            Jeg har modtaget din henvendelse og vil vende tilbage til dig så hurtigt som muligt, typisk inden for 24 timer.
          </p>
          <button
            onClick={() => setStatus("idle")}
            className="mt-8 px-6 py-2.5 rounded-full border border-neutral-200 hover:border-brand-500/30 text-neutral-700 hover:text-brand-700 transition-all duration-200 text-sm font-medium"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Honeypot field - absolutely hidden from humans */}
          <div className="sr-only absolute pointer-events-none" aria-hidden="true">
            <label htmlFor="website_url">Do not fill this out if you are human</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-neutral-700 mb-2">
                Navn <span className="text-brand-600">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Dit fulde navn"
                className={`w-full px-4 py-3 bg-neutral-50 border rounded-xl text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-1 focus:ring-brand-500/50 transition-all duration-200 ${
                  errors.name ? "border-red-300 focus:border-red-400" : "border-neutral-200 focus:border-brand-500"
                }`}
              />
              {errors.name && <p className="mt-1.5 text-xs text-red-500 font-medium">{errors.name}</p>}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-neutral-700 mb-2">
                Telefonnummer <span className="text-brand-600">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="F.eks. +45 40 93 85 93"
                className={`w-full px-4 py-3 bg-neutral-50 border rounded-xl text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-1 focus:ring-brand-500/50 transition-all duration-200 ${
                  errors.phone ? "border-red-300 focus:border-red-400" : "border-neutral-200 focus:border-brand-500"
                }`}
              />
              {errors.phone && <p className="mt-1.5 text-xs text-red-500 font-medium">{errors.phone}</p>}
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-neutral-700 mb-2">
              Email <span className="text-brand-600">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="din.email@adresse.dk"
              className={`w-full px-4 py-3 bg-neutral-50 border rounded-xl text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-1 focus:ring-brand-500/50 transition-all duration-200 ${
                errors.email ? "border-red-300 focus:border-red-400" : "border-neutral-200 focus:border-brand-500"
              }`}
            />
            {errors.email && <p className="mt-1.5 text-xs text-red-500 font-medium">{errors.email}</p>}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-xs font-semibold uppercase tracking-wider text-neutral-700 mb-2">
              Beskrivelse / Besked <span className="text-brand-600">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="5"
              placeholder="Skriv din besked eller beskriv hvad du ønsker hjælp til..."
              className={`w-full px-4 py-3 bg-neutral-50 border rounded-xl text-neutral-900 placeholder-neutral-600 focus:outline-none focus:ring-1 focus:ring-brand-500/50 transition-all duration-200 resize-none ${
                errors.description ? "border-red-300 focus:border-red-400" : "border-neutral-200 focus:border-brand-500"
              }`}
            />
            {errors.description && <p className="mt-1.5 text-xs text-red-500 font-medium">{errors.description}</p>}
          </div>

          {/* Error Message */}
          {status === "error" && (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <p>{errorMessage}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === "loading"}
            className="w-full md:w-auto px-8 py-4 bg-brand-600 hover:bg-brand-700 disabled:bg-neutral-200 disabled:text-neutral-400 text-white hover:text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all duration-200 cursor-pointer disabled:cursor-not-allowed shadow-lg shadow-brand-600/10"
          >
            {status === "loading" ? (
              <>
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Sender besked...
              </>
            ) : (
              <>
                Send besked
                <Send className="w-4 h-4" />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
